/**
 * Voice calibration ledger — Agents as Users Phase 2 (AGENTS-AS-USERS-PRD §4).
 *
 * "Agents audition; humans decide when they're worth counting." After a
 * session's results are published (human authority act), counted estimator
 * responses are compared against the published official values and the
 * comparison is appended to voice_calibration_records (migration 602 —
 * append-only, unique per voice/session/item, so recording is idempotent).
 *
 * Called fire-and-forget from the publish flow: recording failure never
 * blocks or reverses a publish.
 */

import { getSafeSupabaseClient } from '../../lib/supabaseSync';

const LEDGER = 'voice_calibration_records';

export interface OfficialResultRow {
  item_id: string;
  score: number | null;
  rank: number;
}

export interface CountedVoiceResponseRow {
  voice_id: string;
  workspace_id: string;
  results: Array<{ id: string; score?: number }>;
}

export interface CalibrationRow {
  workspace_id: string;
  voice_id: string;
  session_id: string;
  item_id: string;
  estimate: number;
  official_value: number;
  abs_error: number;
}

/**
 * Pure comparison: one row per (voice, item) where the counted response has
 * a numeric estimate and the published results carry the same item. The
 * official value is the published score, falling back to rank when the tool
 * produced no score. abs_error = |estimate − official|.
 */
export function computeCalibrationRows(
  sessionId: string,
  official: OfficialResultRow[],
  responses: CountedVoiceResponseRow[],
): CalibrationRow[] {
  const officialByItem = new Map(official.map((r) => [r.item_id, r]));
  const rows: CalibrationRow[] = [];
  const seen = new Set<string>();
  for (const resp of responses) {
    for (const item of resp.results ?? []) {
      if (typeof item.score !== 'number' || !Number.isFinite(item.score)) continue;
      const officialRow = officialByItem.get(item.id);
      if (!officialRow) continue;
      const officialValue = officialRow.score ?? officialRow.rank;
      if (typeof officialValue !== 'number' || !Number.isFinite(officialValue)) continue;
      const key = `${resp.voice_id}:${item.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        workspace_id: resp.workspace_id,
        voice_id: resp.voice_id,
        session_id: sessionId,
        item_id: item.id,
        estimate: item.score,
        official_value: officialValue,
        abs_error: Math.abs(item.score - officialValue),
      });
    }
  }
  return rows;
}

/**
 * Record calibration rows for a published session. Reads the durable
 * official results (session_results) and the session's counted Voice
 * responses, writes the comparison to the append-only ledger. Idempotent:
 * duplicates are dropped by the unique constraint (ignoreDuplicates), so
 * re-publishing or racing calls are harmless.
 */
export async function recordVoiceCalibration(
  sessionId: string,
): Promise<{ recorded: number; error: string | null }> {
  try {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { recorded: 0, error: 'Supabase not available' };

    const { data: officialRows, error: officialError } = await supabase
      .from('session_results')
      .select('item_id, score, rank')
      .eq('session_id', sessionId);
    if (officialError) return { recorded: 0, error: officialError.message };
    if (!officialRows || officialRows.length === 0) return { recorded: 0, error: null };

    const { data: toolSessions, error: toolError } = await supabase
      .from('tool_sessions')
      .select('id')
      .eq('session_id', sessionId);
    if (toolError) return { recorded: 0, error: toolError.message };
    const toolSessionIds = (toolSessions ?? []).map((t) => (t as { id: string }).id);
    if (toolSessionIds.length === 0) return { recorded: 0, error: null };

    const { data: responses, error: respError } = await supabase
      .from('tool_session_responses')
      .select('voice_id, workspace_id, results')
      .in('tool_session_id', toolSessionIds)
      .eq('principal_type', 'voice')
      .eq('counting_policy', 'counted')
      .not('voice_id', 'is', null);
    if (respError) return { recorded: 0, error: respError.message };

    const rows = computeCalibrationRows(
      sessionId,
      (officialRows ?? []) as OfficialResultRow[],
      (responses ?? []) as CountedVoiceResponseRow[],
    );
    if (rows.length === 0) return { recorded: 0, error: null };

    // Append-only ledger: ON CONFLICT DO NOTHING via ignoreDuplicates —
    // existing rows are never updated (no UPDATE policy exists anyway).
    const { error: insertError } = await supabase
      .from(LEDGER)
      .upsert(rows, { onConflict: 'voice_id,session_id,item_id', ignoreDuplicates: true });
    if (insertError) return { recorded: 0, error: insertError.message };
    return { recorded: rows.length, error: null };
  } catch (err) {
    return { recorded: 0, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export interface VoiceCalibrationSummary {
  count: number;
  meanAbsError: number | null;
  sessionCount: number;
}

/** Simple accuracy summary for the Voice detail page. */
export function summarizeCalibration(
  rows: Array<{ abs_error: number; session_id: string }>,
): VoiceCalibrationSummary {
  if (rows.length === 0) return { count: 0, meanAbsError: null, sessionCount: 0 };
  const total = rows.reduce((sum, r) => sum + Number(r.abs_error ?? 0), 0);
  return {
    count: rows.length,
    meanAbsError: total / rows.length,
    sessionCount: new Set(rows.map((r) => r.session_id)).size,
  };
}

/** Read the ledger and summarize accuracy for one Voice. */
export async function getVoiceCalibrationSummary(
  voiceId: string,
): Promise<{ data: VoiceCalibrationSummary; error: string | null }> {
  const empty: VoiceCalibrationSummary = { count: 0, meanAbsError: null, sessionCount: 0 };
  try {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { data: empty, error: null };
    const { data, error } = await supabase
      .from(LEDGER)
      .select('abs_error, session_id')
      .eq('voice_id', voiceId);
    if (error) return { data: empty, error: error.message };
    return {
      data: summarizeCalibration(
        (data ?? []) as Array<{ abs_error: number; session_id: string }>,
      ),
      error: null,
    };
  } catch (err) {
    return { data: empty, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
